import * as React from 'react'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet'
import ScrollToTopOnMount from '../components/Effects/ScrollToTopOnMount'
import TopNavigation from '../components/Navigation/TopNavigation'
import { AppState } from '../store'
import { WithTranslation, withTranslation } from 'react-i18next'
import { matchPath, Redirect, Route, RouteComponentProps, Switch } from 'react-router'
import ERoute from '../ERoute'
import FullPageContent from '../components/Page/FullPageContent'
import { CurrentUser, Sounds, WorkspaceChannelEvent, WorkspaceChannelEventType } from '../types'
import styled, { css } from 'styled-components'
import { Style } from '../styles'
import Icon from '../components/Icons/Icon'
import InboxView, { InboxViewMode } from '../components/Inbox/InboxView'
import Header from '../components/Inbox/Header'
import HeaderTitle from '../components/Inbox/HeaderTitle'
import { ConversationsController } from '../controllers'
import ActionCableConsumer from '../consumers/ActionCableConsumer'
import Sound from '../audio'
import Utils from '../utilities/Utils'

const InboxOverviewContainer = styled.div`
	display: flex;
	flex-direction: row;
	height: 100%;
	position: relative;
	background: #eff0eb;

	@media screen and (max-width: ${Style.breakpoints.SMALL}) {
		width: 100%;
	}
`

const InboxNavigationSidebar = styled.div<{ active: boolean, mobileActive: boolean }>`
	display: none;
	flex-direction: column;
	flex: 1;
	background: white;
	border-right: 1px solid ${Style.color.border};
	max-width: 230px;
	min-width: 230px;
	z-index: 5;

	${props => props.active && css`
		display: flex;
	`}
	
	@media screen and (max-width: ${Style.breakpoints.SMALL}) {
		display: none;
		position: absolute;
		max-width: initial;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		z-index: 12;

		${props => props.mobileActive && css`
			display: flex;
		`}
	}
`

const InboxNavigationSidebarContent = styled.div`
	padding: 12px;
`

const InboxNavigationItem = styled.div<{ active?: boolean }>`
	border-radius: 5px;
	border: 1px solid transparent;
	padding: 4px;
	margin-bottom: 4px;
	transition: none;
	min-height: 31px;
	cursor: pointer;
	display: flex;
	flex-direction: row;
	align-items: center;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

  &:hover {
		border-color: ${Style.color.border};
		border-style: dashed;
	}

	${props => props.active && css`
		background-color: hsl(0,0%,95%);
		border-color: ${Style.color.border};
		border-style: solid;

		&:hover {
			border-color: ${Style.color.border};
			border-style: solid;
		}
	`}
`

const InboxNavigationItemIcon = styled.div`
  margin-left: ${Style.spacing.x1};
  margin-right: ${Style.spacing.x1_5};
  background: #f7f7fa;
  width: 24px;
  height: 24px;
	min-width: 24px;
	min-height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  font-size: 16px;

  svg {
    width: 16px;
    height: 16px;
		fill: black;
  }

	i {
		color: black;
	}
`

const InboxNavigationItemContent = styled.div`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	width: 100%;
`

const InboxNavigationItemCounter = styled.div`
	padding-right: 8px;
`

const InboxNavigationContent = styled.div`
	height: 100%;
	flex: 1;
	background: #f7f7fa;
	width: 100%;
	overflow: hidden;

	@media screen and (max-width: ${Style.breakpoints.SMALL}) {
		margin-left: 0;
	}
`

interface IStateToProps {
	currentUser: CurrentUser
}

interface IDispatchToProps {
}

type IProps = IStateToProps & IDispatchToProps & WithTranslation & RouteComponentProps<any>

interface IState {
	inboxMenuActive: boolean
	mobileInboxMenuActive: boolean
	counters: {
		all: number;
		me: number;
		unassigned: number;
	}
}

class Inbox extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props)

		this.state = {
			mobileInboxMenuActive: false,
			inboxMenuActive: true,
			counters: null
		}

		// @ts-ignore
		this.fetchCounters = Utils.debounce(this.fetchCounters.bind(this), 500)

		this.onToggleInboxMenu = this.onToggleInboxMenu.bind(this)
		this.onNavigateItemClick = this.onNavigateItemClick.bind(this)
		this.onConversationChange = this.onConversationChange.bind(this)
		this.onActionCableReceived = this.onActionCableReceived.bind(this)
	}

	componentDidMount(): void {
		this.fetchCounters()
	}

	async fetchCounters(): Promise<void> {
		try {
			const counters = await ConversationsController.getCounters()
			this.setState({ counters: counters })
		} catch (ex) {
			console.error(ex)
		}
	}


	onToggleInboxMenu() {
		this.setState({
			inboxMenuActive: !this.state.inboxMenuActive,
			mobileInboxMenuActive: !this.state.mobileInboxMenuActive,
		})
	}

	onNavigateItemClick(path: string) {
		this.setState({ mobileInboxMenuActive: false })
		this.props.history.replace(path)
	}

	onConversationChange() {
		this.fetchCounters()
	}

	onActionCableReceived(event: WorkspaceChannelEvent) {
		console.log('[Inbox] received event', event)

		switch (event.type) {
			case WorkspaceChannelEventType.CONVERSATION_CREATE:
			case WorkspaceChannelEventType.CONVERSATION_UPDATE:
			case WorkspaceChannelEventType.CONVERSATION_DELETE:
				if (event.type === WorkspaceChannelEventType.CONVERSATION_CREATE) Sound.play(Sounds.NOTIFICATION_ADMIN)
				this.fetchCounters()
				break
		}
	}

	render() {
		const { t, location, currentUser } = this.props
		const { inboxMenuActive, mobileInboxMenuActive, counters } = this.state
		const activePath = location.pathname

		return (
			<>
				<Helmet>
					<title>{t('Inbox::{{__appName}} | Inbox')}</title>
				</Helmet>

				<ScrollToTopOnMount />

				<TopNavigation
					icon='inbox'
					title={t('Inbox::Inbox')}
				/>

				<ActionCableConsumer
					channel={{ channel: 'WorkspaceChannel', id: currentUser.workspace.id }}
					onReceived={this.onActionCableReceived}
				>

					<FullPageContent>
						<InboxOverviewContainer>
							<InboxNavigationSidebar active={inboxMenuActive} mobileActive={mobileInboxMenuActive}>
								<Header style={{ paddingLeft: 12, paddingRight: 12 }}>
									<HeaderTitle>{t('Inbox::Inbox')}</HeaderTitle>
								</Header>

								<InboxNavigationSidebarContent>
									<InboxNavigationItem
										active={!!matchPath(activePath, { path: ERoute.PATH_INBOX_ALL })}
										onClick={() => this.onNavigateItemClick(ERoute.PATH_INBOX_ALL)}
									>
										<InboxNavigationItemIcon>
											<Icon icon='contacts' />
										</InboxNavigationItemIcon>

										<InboxNavigationItemContent>
											{t('Inbox::All')}
										</InboxNavigationItemContent>

										<InboxNavigationItemCounter>
											{counters?.all || 0}
										</InboxNavigationItemCounter>
									</InboxNavigationItem>

									<InboxNavigationItem
										active={!!matchPath(activePath, { path: ERoute.PATH_INBOX_ME })}
										onClick={() => this.onNavigateItemClick(ERoute.PATH_INBOX_ME)}
									>
										<InboxNavigationItemIcon>
											<Icon icon='user' />
										</InboxNavigationItemIcon>

										<InboxNavigationItemContent>
											{t('Inbox::Your inbox')}
										</InboxNavigationItemContent>

										<InboxNavigationItemCounter>
											{counters?.me || 0}
										</InboxNavigationItemCounter>
									</InboxNavigationItem>

									<InboxNavigationItem
										active={!!matchPath(activePath, { path: ERoute.PATH_INBOX_UNASSIGNED })}
										onClick={() => this.onNavigateItemClick(ERoute.PATH_INBOX_UNASSIGNED)}
									>
										<InboxNavigationItemIcon>
											<Icon icon='messenger-unassigned' />
										</InboxNavigationItemIcon>

										<InboxNavigationItemContent>
											{t('Inbox::Unassigned')}
										</InboxNavigationItemContent>

										<InboxNavigationItemCounter>
											{counters?.unassigned || 0}
										</InboxNavigationItemCounter>
									</InboxNavigationItem>
								</InboxNavigationSidebarContent>
							</InboxNavigationSidebar>

							<InboxNavigationContent>
								<Switch>
									<Route path={ERoute.PATH_INBOX_ME}>
										<InboxView
											key='inbox-me'
											mode={InboxViewMode.INBOX}
											onToggleInboxMenu={this.onToggleInboxMenu}
										/>
									</Route>
									<Route path={ERoute.PATH_INBOX_ALL}>
										<InboxView
											key='inbox-all'
											mode={InboxViewMode.ALL}
											onToggleInboxMenu={this.onToggleInboxMenu}
										/>
									</Route>
									<Route path={ERoute.PATH_INBOX_UNASSIGNED}>
										<InboxView
											key='unassigned'
											mode={InboxViewMode.UNASSIGNED}
											onToggleInboxMenu={this.onToggleInboxMenu}
										/>
									</Route>

									<Redirect to={ERoute.PATH_INBOX_ALL} />
								</Switch>
							</InboxNavigationContent>
						</InboxOverviewContainer>
					</FullPageContent>
				</ActionCableConsumer>
			</>
		)
	}
}

const mapStateToProps = (state: AppState): IStateToProps => {
	const {
		authentication: {
			currentUser,
		}
	} = state

	return {
		currentUser: currentUser,
	}
}

const mapDispatchToProps: IDispatchToProps = {
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Inbox))